/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import {PlayButton, VolumeButtonAndSlider, FullScreenButton, Seekbar, RewindButton, LatencyText, VideoQualityButton} from './controls';
import {PlaybackControls} from './PlaybackControls';

export class PlaybackControlsBuilder {
  constructor({videoElementRef, forceUpdate, canAudioStateBeChanged, areControlsShown = true, showControls = () => {}}) {
    this._videoElement = videoElementRef?.current;
    this._controlComponents = {
      leftColumnControls: [],
      rightColumnControls: [],
      floatingControls: []
    };
    this._canAudioStateBeChanged = canAudioStateBeChanged;
    this._forceUpdate = forceUpdate;
    this._areControlsShown = areControlsShown;
    this._showControls = showControls;
  }

  withPlayButton() {
    const isPaused = this._videoElement?.paused ?? true;
    const play = async() => {
      try {
        await this._videoElement?.play();
        this._forceUpdate();
      } catch (error) {
        console.warn('Failed to play video:', error);
      }
    };

    const pause = () => {
      this._videoElement?.pause();
      this._forceUpdate();
    };

    this._controlComponents.floatingControls.push(<PlayButton paused={!isPaused} play={play} pause={pause} />);

    return this;
  }

  withAudioControls() {
    const noop = () => {};

    const changeVolume = level => {
      this._videoElement.volume = level;
      this._forceUpdate();
    };

    const toggleMuteState = () => {
      this._videoElement.muted = !this._videoElement.muted;
      this._forceUpdate();
    };

    this._controlComponents.leftColumnControls.push(
      <VolumeButtonAndSlider
        isVolumeSliderShown={!this._videoElement?.muted}
        showVolumeSlider={noop}
        hideVolumeSlider={noop}
        volume={this._videoElement?.volume ?? 0}
        changeVolume={changeVolume}
        canAudioStateBeChanged={this._canAudioStateBeChanged}
        changeMuteState={toggleMuteState}
        muted={this._videoElement?.muted ?? true}
      />
    );

    return this;
  }

  withFullscreenControls(isFullscreen) {
    const requestFullScreenForElement = element => {
      if (!element) {
        throw new Error('Element required to request full screen');
      }

      // Ensure the video container's (parent) goes fullscreen, not just the video element
      const videoContainer = element.parentElement.parentElement;
      const targetElement = videoContainer || element;

      if (targetElement.requestFullscreen) {
        targetElement.requestFullscreen();
      } else if (targetElement.mozRequestFullScreen) {
        targetElement.mozRequestFullScreen();
      } else if (targetElement.webkitRequestFullScreen) {
        targetElement.webkitRequestFullScreen();
      } else if (targetElement.msRequestFullscreen) {
        targetElement.msRequestFullscreen();
      }
    };

    const exitFullscreen = () => {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    };

    const changeFullscreenState = (toFullscreen) => {
      if (toFullscreen) {
        requestFullScreenForElement(this._videoElement);

        return;
      }

      exitFullscreen();

      this._forceUpdate();
    };

    this._controlComponents.rightColumnControls.push(
      <FullScreenButton changeFullscreenState={changeFullscreenState} isFullscreen={isFullscreen} />
    );

    return this;
  }

  withSeekControls() {
    const noop = () => {};

    const seekTo = time => {
      this._videoElement.currentTime = time;
      this._forceUpdate();
    };

    this._controlComponents.floatingControls.push(
      <Seekbar
        seekTo={seekTo}
        seekToRealtimeStream={noop}
        bufferDuration={this._videoElement.duration}
        currentTime={this._videoElement.currentTime}
        lag={0}
        pointOfTime={''}
        mouseEnter={noop}
        mouseLeave={noop}
        mouseMove={noop}
      />
    );

    return this;
  }

  withRewindControls() {
    const noop = () => {};

    const rewind = () => {
      this._videoElement.currentTime -= 10;
      this._forceUpdate();
    };

    this._controlComponents.floatingControls.push(
      <RewindButton rewind={rewind} amountToRewindStream={10} mouseEnter={noop} mouseLeave={noop} mouseMove={noop} />
    );

    return this;
  }

  withLatencyTextDisplay() {
    const noop = () => {};

    const latency = 0;

    this._controlComponents.floatingControls.push(
      <LatencyText latency={latency} mouseEnter={noop} mouseLeave={noop} mouseMove={noop} />
    );

    return this;
  }

  withVideoQualityControls() {
    this._controlComponents.rightColumnControls.push(
      <VideoQualityButton />
    );

    return this;
  }

  build() {
    const {leftColumnControls, rightColumnControls, floatingControls} = this._controlComponents;

    return (
      <PlaybackControls
        areControlsShown={this._areControlsShown}
        showControls={this._showControls}
        leftColumnControls={leftColumnControls}
        rightColumnControls={rightColumnControls}
        floatingControls={floatingControls}
      />
    );
  }
}